/**
 * THOR wishlist — persisted in localStorage, syncs hearts + header count + wishlist page.
 */
(() => {
  if (window.thorWishlist) return;

  const KEY = 'thor-wishlist-v1';
  const eventName = 'thor:wishlist-change';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (_) {
      return [];
    }
  };

  const write = (list) => {
    localStorage.setItem(KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(eventName, { detail: { items: list } }));
  };

  const has = (id) => read().some((item) => String(item.id) === String(id));

  const toggle = (item) => {
    const list = read();
    const id = String(item.id);
    const index = list.findIndex((entry) => String(entry.id) === id);
    if (index >= 0) {
      list.splice(index, 1);
      write(list);
      return false;
    }
    list.unshift({
      id,
      handle: item.handle || '',
      title: item.title || 'Product',
      url: item.url || `/products/${item.handle || ''}`,
      image: item.image || '',
      price: item.price || '',
      addedAt: Date.now(),
    });
    write(list);
    return true;
  };

  const remove = (id) => {
    write(read().filter((item) => String(item.id) !== String(id)));
  };

  const syncButtons = (root = document) => {
    root.querySelectorAll('[data-product-wish]').forEach((button) => {
      const id = button.getAttribute('data-product-id');
      if (!id) return;
      const on = has(id);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
    });
  };

  const syncCount = () => {
    const count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      if (count > 0) {
        el.hidden = false;
        el.textContent = String(count);
      } else {
        el.hidden = true;
        el.textContent = '';
      }
    });
  };

  const bindButtons = (root = document) => {
    root.querySelectorAll('[data-product-wish]').forEach((button) => {
      if (button.dataset.wishBound) return;
      button.dataset.wishBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const item = {
          id: button.getAttribute('data-product-id'),
          handle: button.getAttribute('data-product-handle'),
          title: button.getAttribute('data-product-title'),
          url: button.getAttribute('data-product-url'),
          image: button.getAttribute('data-product-image'),
          price: button.getAttribute('data-product-price'),
        };
        if (!item.id) return;
        const saved = toggle(item);
        const toast = window.thorToast || window.toast;
        if (saved) toast?.success?.('Saved to wishlist');
        else toast?.remove?.('Removed from wishlist');
        syncButtons(document);
        syncCount();
      });
    });
  };

  const renderPage = () => {
    const grid = document.querySelector('[data-wishlist-grid]');
    const empty = document.querySelector('[data-wishlist-empty]');
    if (!grid || !empty) return;

    const items = read();
    grid.innerHTML = '';

    if (!items.length) {
      empty.hidden = false;
      grid.hidden = true;
      return;
    }

    empty.hidden = true;
    grid.hidden = false;

    items.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'wishlist-card';
      card.innerHTML = `
        <a class="wishlist-card__media" href="${item.url}">
          ${
            item.image
              ? `<img class="wishlist-card__image" src="${item.image}" alt="${item.title.replace(/"/g, '&quot;')}" loading="lazy" width="400" height="500">`
              : `<div class="wishlist-card__placeholder"></div>`
          }
        </a>
        <div class="wishlist-card__info">
          <h2 class="wishlist-card__title"><a href="${item.url}"></a></h2>
          <p class="wishlist-card__price"></p>
          <div class="wishlist-card__actions">
            <a class="wishlist-card__btn" href="${item.url}">View product</a>
            <button type="button" class="wishlist-card__remove" data-wishlist-remove="${item.id}">Remove</button>
          </div>
        </div>
      `;
      card.querySelector('.wishlist-card__title a').textContent = item.title;
      card.querySelector('.wishlist-card__price').textContent = item.price || '';
      grid.appendChild(card);
    });

    grid.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        remove(btn.getAttribute('data-wishlist-remove'));
        const toast = window.thorToast || window.toast;
        toast?.remove?.('Removed from wishlist');
        renderPage();
        syncButtons(document);
        syncCount();
      });
    });
  };

  const init = () => {
    bindButtons(document);
    syncButtons(document);
    syncCount();
    renderPage();
  };

  window.thorWishlist = { read, write, has, toggle, remove, syncButtons, syncCount, renderPage, bindButtons };

  window.addEventListener(eventName, () => {
    syncButtons(document);
    syncCount();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
